/**
 * @file MeetingScoreStrip.tsx
 * @brief Read Score / Engagement / Sentiment (+ situational) analytics gauges.
 *
 * @details
 * Web parity with the mobile MeetingIntel analytics row
 * (`FRONTEND/.../MeetingDetails/tabs/MeetingIntelTab.tsx` +
 * `components/ProgressCircle.tsx`): circular gauges with GOOD/OKAY/POOR
 * badge pills. On detail pages (`variant="cell"`) each metric is its own
 * `GridCell` so hairline borders match the rest of the panel section grid.
 * `MeetingScoreChip` is the one-metric compact pill used in the list tables.
 *
 * @dependencies
 * - shared/theme/cn
 * - features/analytics/components/GridBlock
 *
 * @references
 * - BACKEND/src/features/boardroom/metrics.service.mjs (score + badge source)
 * - FRONTEND/.../MeetingDetails/components/ProgressCircle.tsx (mobile gauge)
 *
 * @author DES
 * @date 08-07-2026
 * @updated 29-07-2026 — cell variant uses GridCells / GridCell
 * @updated 20-10-2026 — each score names what it measures (Readability,
 *   Participation, Tone, Follow-through…) above the ring, shows its detail
 *   line, and has an (i) saying how it is worked out (text matches
 *   metrics.service.mjs). The GOOD / OKAY / POOR pill is neutral grey; only
 *   the ring stays brand red, so red no longer reads as "bad" (DES).
 */

import { useState } from "react";
import { Info } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import type { MeetingMetric, MeetingMetrics } from "../schemas";
import {
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";

// ----------------------------------------------------------------------
// Badge → tone. `text` drives the ring's arc colour (via currentColor);
// `badge` is no longer used for the pill (neutral since 20-10-2026).
// ----------------------------------------------------------------------
/**
 * ⏳ TRIAL, as the phone's `meetingScorePalette` (DES 08-10-2026, picked
 * colour option 2): brand red only, the score's strength is the red's
 * strength (GOOD 100%, OKAY 60%, POOR 33%), every badge on one faint red
 * wash. false = the green / amber / red traffic light below.
 */
export const MEETING_SCORE_BRAND_RED = true;

const BRAND_TONES: Record<string, { text: string; badge: string }> = {
  GOOD: {
    text: "text-[rgba(229,36,33,1)]",
    badge: "bg-[rgba(229,36,33,0.13)] text-[rgba(229,36,33,1)]",
  },
  OKAY: {
    text: "text-[rgba(229,36,33,0.6)]",
    badge: "bg-[rgba(229,36,33,0.13)] text-[rgba(229,36,33,0.6)]",
  },
  POOR: {
    text: "text-[rgba(229,36,33,0.33)]",
    badge: "bg-[rgba(229,36,33,0.13)] text-[rgba(229,36,33,0.33)]",
  },
};

function toneForBadge(badge?: string | null): {
  text: string;
  badge: string;
} {
  if (MEETING_SCORE_BRAND_RED && badge && BRAND_TONES[badge]) {
    return BRAND_TONES[badge];
  }
  switch (badge) {
    case "GOOD":
      return { text: "text-success", badge: "bg-success-soft text-success" };
    case "OKAY":
      return { text: "text-warning", badge: "bg-warning-soft text-warning" };
    case "POOR":
      return {
        text: "text-destructive",
        badge: "bg-destructive/15 text-destructive",
      };
    default:
      return { text: "text-muted-foreground", badge: "bg-muted text-muted-foreground" };
  }
}

// ----------------------------------------------------------------------
// Circular gauge — SVG ring (muted track + badge-coloured arc), score in the
// centre. Mirrors the mobile `ProgressCircle` (arc from 12 o'clock, clockwise).
// ----------------------------------------------------------------------
function Gauge({
  value,
  toneText,
  size = 56,
  // 3, was 5: too thick, as the phone (DES 08-10-2026).
  strokeWidth = 3,
}: {
  value: number;
  toneText: string;
  size?: number;
  strokeWidth?: number;
}) {
  const score = Math.max(0, Math.min(100, Math.round(value)));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - score / 100);
  const center = size / 2;

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className={toneText}>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-muted"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[15px] font-extrabold tabular-nums text-foreground">
          {score}
        </span>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// MeetingScoreChip — one metric as a compact pill (list tables).
// ----------------------------------------------------------------------
export function MeetingScoreChip({
  label,
  metric,
  className,
}: {
  label: string;
  metric: MeetingMetric;
  className?: string;
}) {
  if (!metric) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        NEUTRAL_PILL,
        className,
      )}
    >
      <span className="opacity-70">{metric.label ?? label}</span>
      <span className="font-bold tabular-nums">
        {Math.round(Number(metric.score))}
      </span>
    </span>
  );
}

// ----------------------------------------------------------------------
// The GOOD / OKAY / POOR pill is neutral: grey on light, soft white on dark.
// Red is the brand colour, not a warning, so only the ring carries it
// (DES 20-10-2026). No green dot either.
// ----------------------------------------------------------------------
const NEUTRAL_PILL = "bg-muted text-foreground/70";

const BADGE_RULE = "70 and above is Good, 50 to 69 Okay, under 50 Poor.";

type MetricKey =
  | "readScore"
  | "engagement"
  | "sentiment"
  | "languageBridge"
  | "clientBalance"
  | "followThrough";

/**
 * What each score measures and how it is worked out. Keep in step with
 * BACKEND/src/features/boardroom/metrics.service.mjs and the "clarity" /
 * "sentiment" asks in synthesis.service.mjs. The phone carries the same
 * text (scratch/claude-code-report.md, 20-10-2026).
 */
const METRIC_INFO: Record<MetricKey, { title: string; how: string }> = {
  readScore: {
    title: "Readability",
    how: "How clearly the discussion was put across, in any language: the AI reads the whole transcript and scores it from 0 (hard to follow) to 100 (clear and well structured). Meetings scored before that use a reading-ease formula on the English words.",
  },
  engagement: {
    title: "Participation",
    how: "How evenly the talking was shared between the people who spoke, plus how often the speaker changed (about 8 changes a minute counts in full). The even share is about two thirds of the score.",
  },
  sentiment: {
    title: "Tone",
    how: "The overall mood of the discussion, judged by the AI in any language: 50 is neutral, higher means agreement and confidence, lower means friction or frustration.",
  },
  followThrough: {
    title: "Follow-through",
    how: "How likely the to-dos are to get done. Each to-do's delay risk (hedging, waiting on others, no clear owner) is averaged and turned round, so 100 means every commitment sounded firm. To-dos added by hand are left out.",
  },
  languageBridge: {
    title: "Language mix",
    how: "Shown when two or more languages each make up at least 8% of the meeting. Higher means the languages were used more evenly.",
  },
  clientBalance: {
    title: "Client vs team",
    how: "How much the people from outside your company spoke: their share of the words, where half or more scores 100.",
  },
};

const TALK_BALANCE_INFO = {
  title: "Talk balance",
  how: "How evenly the words were shared between everyone who spoke: 100 means everyone spoke about the same amount.",
};

/** Backend labels that only repeat the metric's name; never shown as the
 *  word beside the ring. */
const NAME_LABELS = new Set([
  "read score",
  "engagement",
  "sentiment",
  "language bridge",
  "client vs team",
  "talk balance",
  "follow-through",
]);

function infoFor(key: MetricKey, metric: NonNullable<MeetingMetric>) {
  if (key === "clientBalance" && metric.label === "Talk Balance") {
    return TALK_BALANCE_INFO;
  }
  return METRIC_INFO[key];
}

// ----------------------------------------------------------------------
// MetricInfo — the (i) beside the name. Hover or click opens it (click so
// it works on touch screens and in the Mac app's widget too).
// ----------------------------------------------------------------------
function MetricInfo({ title, how }: { title: string; how: string }) {
  const [open, setOpen] = useState(false);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`How ${title} is worked out`}
            onClick={(e) => {
              // Radix closes a tooltip on trigger click; a click toggles here.
              e.preventDefault();
              setOpen((v) => !v);
            }}
            className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] leading-snug">
          <p>{how}</p>
          <p className="mt-1 opacity-70">{BADGE_RULE}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ----------------------------------------------------------------------
// MetricColumn — name + (i) on top, then the ring with its word and pill,
// then the detail line when there is one.
// ----------------------------------------------------------------------
function MetricColumn({
  metricKey,
  metric,
  size,
}: {
  metricKey: MetricKey;
  metric: NonNullable<MeetingMetric>;
  size?: number;
}) {
  const tone = toneForBadge(metric.badge);
  const info = infoFor(metricKey, metric);
  const word =
    metric.label && !NAME_LABELS.has(metric.label.trim().toLowerCase())
      ? metric.label
      : null;
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-1">
        <p className="text-xs font-semibold text-foreground">{info.title}</p>
        <MetricInfo title={info.title} how={info.how} />
      </div>
      <div className="flex items-center gap-2.5">
        <Gauge value={Number(metric.score)} toneText={tone.text} size={size} />
        <div className="min-w-0">
          {word ? (
            <p className="mb-1 text-xs font-medium text-muted-foreground">
              {word}
            </p>
          ) : null}
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
              NEUTRAL_PILL,
            )}
          >
            {metric.badge}
          </span>
        </div>
      </div>
      {metric.detail ? (
        <p className="mt-2 max-w-[220px] text-xs leading-snug text-muted-foreground">
          {metric.detail}
        </p>
      ) : null}
    </div>
  );
}

function presentMetrics(metrics: MeetingMetrics) {
  const keys: MetricKey[] = [
    "readScore",
    "engagement",
    "sentiment",
    "languageBridge",
    "clientBalance",
    "followThrough",
  ];
  return keys
    .map((key) => ({ key, metric: metrics[key] ?? null }))
    .filter(
      (m): m is { key: MetricKey; metric: NonNullable<MeetingMetric> } =>
        Boolean(m.metric),
    );
}

function cellsClassForCount(count: number): string {
  if (count <= 2) return "grid-cols-1 sm:grid-cols-2";
  if (count === 3) return "grid-cols-1 sm:grid-cols-3";
  if (count === 4) return "grid-cols-2 lg:grid-cols-4";
  if (count === 5) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5";
  return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6";
}

// ----------------------------------------------------------------------
// MeetingScoreStrip — metric gauges; `cell` = GridCells row on detail pages.
// ----------------------------------------------------------------------
export function MeetingScoreStrip({
  metrics,
  className,
  variant = "card",
}: {
  metrics?: MeetingMetrics | null;
  className?: string;
  /** `cell` renders each metric as a GridCell in the parent GridBlock.
   *  `row` is the phone's score row (30-09-2026): one scrolling line, 52px
   *  gauges, 18px apart, no card or dividers (mobile MeetingIntelTab). */
  variant?: "card" | "cell" | "row";
}) {
  if (!metrics) return null;

  const present = presentMetrics(metrics);
  if (present.length === 0) return null;

  if (variant === "row") {
    return (
      <div
        className={cn(
          "flex gap-[18px] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
        dir="ltr"
      >
        {present.map((m) => (
          <div key={m.key} className="shrink-0">
            <MetricColumn metricKey={m.key} metric={m.metric} size={52} />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "cell") {
    return (
      <GridCells className={cn(cellsClassForCount(present.length), className)}>
        {present.map((m) => (
          <GridCell key={m.key}>
            <MetricColumn metricKey={m.key} metric={m.metric} />
          </GridCell>
        ))}
      </GridCells>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border/60 bg-background px-5 py-4",
        className,
      )}
    >
      <div className="flex items-start gap-5 overflow-x-auto">
        {present.map((m, i) => (
          <div key={m.key} className="flex shrink-0 items-stretch gap-5">
            {i > 0 ? <div className="w-px shrink-0 bg-border" /> : null}
            <MetricColumn metricKey={m.key} metric={m.metric} />
          </div>
        ))}
      </div>
    </div>
  );
}
